import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import usePortalAccess from '@/access/usePortalAccess';
import ProtectedRoute from '@/components/ProtectedRoute';
import { useAuth } from '@/contexts/AuthContext';
import {
  buildPortalAccessContext,
  getDefaultHomePath,
  getRegisteredProtectedRoutes,
  PUBLIC_ROUTES,
} from '@/config/portalRoutes.config';
import { getClientPostAuthPath } from '@core/portal/clientPortal.util';
import ClientOnboardingGate from '@/routes/ClientOnboardingGate';
import { authPageLayout, protectedAppPageLayout } from '@/routes/routeLayouts';
import { ClientOnboarding } from '@/pages';
import { usePortalPath } from '@/hooks/usePortalPath';

const renderPublicRoutes = (
  routes: typeof PUBLIC_ROUTES,
  wrapPage: (page: React.ReactNode) => React.ReactNode,
) =>
  routes.map((route) => {
    const RouteComponent = route.component;

    return (
      <Route
        key={route.id}
        path={route.path}
        element={wrapPage(<RouteComponent />)}
      />
    );
  });

const renderProtectedRoutes = (
  access: ReturnType<typeof usePortalAccess>,
  wrapPage: (
    route: ReturnType<typeof getRegisteredProtectedRoutes>[number],
    page: React.ReactNode,
  ) => React.ReactNode,
) =>
  getRegisteredProtectedRoutes(access).map((route) => {
    const RouteComponent = route.component;

    return (
      <Route
        key={route.id}
        path={route.path}
        element={wrapPage(route, <RouteComponent />)}
      />
    );
  });

const RootRedirect = () => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { path: portalPath } = usePortalPath();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAuthenticated || !user?.role) {
    return <Navigate to={portalPath('/login')} replace />;
  }

  const access = buildPortalAccessContext(user.role);
  const homePath = access.isClientPortal
    ? getClientPostAuthPath(user)
    : getDefaultHomePath(access);

  return <Navigate to={portalPath(homePath)} replace />;
};

const AppRoutes = () => {
  const access = usePortalAccess();

  return (
    <Routes>
      {renderPublicRoutes(PUBLIC_ROUTES, authPageLayout)}

      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <ClientOnboardingGate>
              {authPageLayout(<ClientOnboarding />)}
            </ClientOnboardingGate>
          </ProtectedRoute>
        }
      />

      <Route path="/" element={<RootRedirect />} />
      {renderProtectedRoutes(access, (route, page) => (
        <ClientOnboardingGate key={route.id}>
          {protectedAppPageLayout(route, page)}
        </ClientOnboardingGate>
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
